import type { EntityId, EntityState } from '@reduxjs/toolkit';

// maps names of referenced types to arrays of entity ids
export type References<K extends string, ID extends EntityId = string> = {
  [E in K]: Record<ID, boolean>;
};

export function createReferenceAdapter<
  R extends string,
  Entity extends { references?: References<R, ID> },
  ID extends EntityId = string,
>(types: readonly R[]) {
  return {
    createInitialState: (
      initialState: Partial<References<R, ID>> | undefined = undefined,
    ) => {
      const defaultState = types.reduce(
        (acc, type) => {
          return {
            ...acc,
            [type]: {},
          };
        },
        {} as References<R, ID>,
      );

      return initialState ? { ...defaultState, ...initialState } : defaultState;
    },

    addReference: (
      draft: EntityState<Entity, ID>,
      parentId: ID,
      ref: R,
      id: ID,
    ) => {
      const parent = draft.entities[parentId];
      if (!parent) {
        return;
      }

      if (!parent.references || Object.keys(parent.references).length === 0) {
        parent.references = {
          [ref]: {} as Record<ID, boolean>,
        } as References<R, ID>;
      }

      const references = parent.references[ref];
      if (!references[id]) {
        references[id] = true;
      }
    },
    removeReference: (
      draft: EntityState<Entity, ID>,
      parentId: ID,
      ref: R,
      id: ID,
    ) => {
      const parent = draft.entities[parentId];
      if (!parent || !parent.references) {
        return;
      }

      const references = parent.references[ref];
      if (!references) {
        return;
      }

      delete references[id];
    },
    removeReferencePrefix: (
      draft: EntityState<Entity, ID>,
      parentId: ID,
      ref: R,
      prefix: string,
    ) => {
      const parent = draft.entities[parentId];
      if (!parent || !parent.references) {
        return;
      }

      const references = parent.references[ref];
      if (!references) {
        return;
      }

      Object.keys(references)
        .filter(key => key.startsWith(prefix))
        .forEach(key => {
          delete references[key as ID];
        });
    },

    selectors: {
      selectReferences:
        (state: EntityState<Entity, ID>) =>
        (parentId: ID, ref: R): ID[] => {
          const parent = state.entities[parentId];
          if (!parent || !parent.references) {
            return [];
          }

          return Object.keys(parent.references[ref]) as ID[];
        },

      selectParentsByRef: (
        state: EntityState<Entity, ID>,
        ref: R,
        targetId: ID,
      ): ID[] => {
        return state.ids
          .filter(parentId => {
            const parent = state.entities[parentId];
            if (!parent || !parent.references) {
              return false;
            }

            const refs = parent.references[ref];
            return refs[targetId] !== undefined;
          })
          .map(parentId => parentId);
      },
    },
  };
}
